import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The field-label rung — design-grammar §2, v3.2 ("A field label is content,
 * not metadata", DECISIONS 2026-08-26).
 *
 * v3.2 moved field labels off the 11px `label` rung and onto `body` 500 `ink`,
 * but it delivered that through `EditorialField` and the shadcn `Label` only.
 * Every surface that hand-rolled `<label htmlFor="…"><MonoLabel>…</MonoLabel></label>`
 * kept rendering its question SMALLER than the box the answer is typed into —
 * 81 of them by 2026-08-27 (#1204), because a rule that lives in two primitives
 * cannot reach markup that calls neither. This primitive is the third delivery
 * route: the one that works where the markup will not accept `EditorialField`.
 *
 * The test for whether text belongs here: **does the user act on this text, or
 * does it describe the thing beside it?** A question the user must answer is
 * acted on → `FieldLabel`. An eyebrow, a chip, a sidebar group label or a
 * key-value key describes → `MonoLabel`, which stays on the 11px rung.
 */

/**
 * Which rung the label sits on. The invariant v3.2 actually protects is that a
 * label is never smaller than the input it labels — so the size follows the
 * INPUT beside it, and there is no third option.
 */
export type FieldLabelSize = 'body' | 'dense';

const SIZE_CLASS: Record<FieldLabelSize, string> = {
  /** Default. The input beside it is `body` (14px) — every family-facing form. */
  body: 'text-body',
  /** The input beside it is `body-sm` (13px) — dense staff rows and filter bars. */
  dense: 'text-body-sm',
};

/** Everything about the rung except its size, so `EditorialField` and a
 *  hand-rolled `<label>` cannot render two different field labels. */
export const FIELD_LABEL_TYPE = 'font-sans font-medium leading-[1.4] text-stevens-ink';

interface FieldLabelProps {
  /** The id of the control this labels. Omit only when the control is nested
   *  INSIDE the label, which is its own valid association. */
  htmlFor?: string;
  /** `legend` for a label over a radio/checkbox group — one question, several
   *  inputs. A heading over SEVERAL questions is not a field label: it is a
   *  section heading and takes `card-title` (v3.3).
   *  `span` for the wrapping-label shape, where the control is nested INSIDE
   *  its own `<label>` — a `<label>` may not contain another `<label>`, so the
   *  rung has to arrive on a span there. */
  as?: 'label' | 'legend' | 'span';
  size?: FieldLabelSize;
  /** Renders the same `aria-hidden` asterisk `EditorialField` uses, so a
   *  required marker looks identical wherever it appears. */
  required?: boolean;
  id?: string;
  className?: string;
  children: ReactNode;
}

export function FieldLabel({
  htmlFor,
  as = 'label',
  size = 'body',
  required,
  id,
  className,
  children,
}: FieldLabelProps) {
  const Tag = as;
  return (
    <Tag
      id={id}
      // Only a real `<label>` takes htmlFor: a `legend` labels its fieldset by
      // position, and a `span` sits inside the label that already does.
      {...(as === 'label' && htmlFor ? { htmlFor } : {})}
      className={cn('block', FIELD_LABEL_TYPE, SIZE_CLASS[size], className)}
    >
      {children}
      {required ? (
        <span aria-hidden="true" className="ml-1 text-[oklch(60%_0.18_25)]">*</span>
      ) : null}
    </Tag>
  );
}
